import { ROUTE_BILLS_PAGE, ROUTE_BILL_DETAILS, ROUTE_HOMEPAGE } from '@1bill-app/constants';
import { generateArray } from '@1bill-app/helpers/array.helper';
import { BillQuery } from '@1bill-app/services/bill/bill.query';
import { BillService } from '@1bill-app/services/bill/bill.service';
import { BillCategoryKey, GetBillDuration, GetBillGroup, GetBillOption, ViewBillType } from '@1bill-app/services/bill/types';
import { HomeService } from '@1bill-app/services/home/home.service';
import { StatusQuery } from '@1bill-app/services/status/status.query';
import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-upcoming-bills',
  templateUrl: './upcoming-bills.component.html',
  styleUrls: ['./upcoming-bills.component.scss'],
  animations: [
    trigger('fadeAnimation', [
      transition(':increment', [style({ opacity: 0 }), animate(800)]),
      transition(':decrement', [style({ opacity: 0 }), animate(800)]),
    ]),
  ],
})
export class UpcomingBillsComponent implements OnInit, OnDestroy {
  @Input('loading') isLoading: boolean;
  // public upcomingBills$ = this.billQuery.getHomeUpcomingBills(
  //   GetBillOption.UNPAID_PLUS_FAILED_BILLS,
  // );
  public hasAnyBills$ = this.billQuery
    .getHomeUpcomingBillsNoFilter()
    .pipe(map((bills) => bills?.length > 0));
  public upcomingBills: ViewBillType[];
  public loading$ = this.billQuery.isLoading$;
  public billCategories$ = this.billQuery.billCategories$;
  public filter$ = this.billQuery.select((state) => state?.ui?.homePageFilter);
  public aggregated$ = this.billQuery.select('aggregated');
  public billCategorySelected = BillCategoryKey.ALL;
  public billDurationSelected: GetBillDuration = GetBillDuration.ALL;
  public billGroupSelected: GetBillGroup.RECENT_BILLS | GetBillGroup.UPCOMING_BILLS = GetBillGroup.RECENT_BILLS;
  public jobsOnProgress$ = this.statusQuery.jobsOnProgress$;
  public billsAggregatedInfo$ = this.billQuery.billsAggregatedInfo$;
  billsAggregatedInfo: {
    upcomingBillsCount: number;
    recentBillsCount: number;
    recentAllDueAmount: number;
    recentMonthlyDueAmount: number;
    recentWeeklyDueAmount: number;
    upcomingAllDueAmount: number;
    upcomingMonthlyDueAmount: number;
    upcomingWeeklyDueAmount: number;
  };
  subs = new SubSink();
  combinedDueAmount: number;
  generateArray = generateArray;

  constructor(
    private router: Router,
    private billQuery: BillQuery,
    private billService: BillService,
    private statusQuery: StatusQuery,
    private logger: NGXLogger,
    private homeService: HomeService,
  ) {}

  ngOnInit() {
    this.subs.sink = this.billQuery.billsAggregatedInfo$.subscribe((agg) => {
      this.billsAggregatedInfo = agg;
      this.updateCombinedDueAmount();
    });

    this.homeService.homeRouteEvent$.subscribe((value) => {
      if (value.trigger) {
        // On this trigger, default to the ALL key
        this.billCategorySelected = BillCategoryKey.ALL;
        this.billGroupSelected = GetBillGroup.RECENT_BILLS;
        switch (value.message) {
          case 'weekTab':
            {
              this.billDurationSelected = GetBillDuration.WEEK;
            }
            break;
          case 'monthTab':
            {
              this.billDurationSelected = GetBillDuration.MONTH;
            }
            break;
          default:
            break;
        }
      }
    });

    this.fetchData(this.billDurationSelected, this.billGroupSelected);
  }

  handleAddBill() {
    this.homeService.addBillEvent();
  }

  fetchData(
    billDuration: GetBillDuration,
    billGroup: GetBillGroup,
  ) {
    this.subs.sink = this.billQuery
      .getHomeUpcomingBills(GetBillOption.UNPAID_PLUS_FAILED_BILLS, billDuration, billGroup)
      .subscribe((res) => {
        this.logger.debug(
          `UpcomingBillsComponent::fetchData() => ${billDuration} ${billGroup} :`,
          res,
        );
        this.upcomingBills = res;
      });
  }

  trackByFn(item: ViewBillType) {
    return item?.billPaymentId;
  }

  navigateToBillPage(billPaymentId?: number, jobId?: number) {
    if (billPaymentId)
      this.router.navigate([ROUTE_BILL_DETAILS], {
        queryParams: { _source: ROUTE_HOMEPAGE, billPaymentId: billPaymentId },
      });
    else
      this.router.navigate([ROUTE_BILL_DETAILS], {
        queryParams: { _source: ROUTE_HOMEPAGE, jobId: jobId },
      });
  }

  gotoBillList() {
    this.router.navigate([ROUTE_BILLS_PAGE]);
  }

  categoryChanged(value: BillCategoryKey) {
    this.billService.updateHomePageFilter({ categoryKey: value });
  }

  durationChanged(value: GetBillDuration) {
    this.logger.debug(`UpcomingBillsComponent::durationChanged() => ${value}`);
    this.updateCombinedDueAmount();
    this.fetchData(value, this.billGroupSelected);
  }

  groupChanged(value: GetBillGroup) {
    this.logger.debug(`UpcomingBillsComponent::durationChanged() => ${value}`);
    this.updateCombinedDueAmount();
    this.fetchData(this.billDurationSelected, value);
  }

  updateCombinedDueAmount() {
    switch (this.billDurationSelected) {
      case GetBillDuration.ALL: {
        switch (this.billGroupSelected) {
          case GetBillGroup.RECENT_BILLS: {
            this.combinedDueAmount = this.billsAggregatedInfo.recentAllDueAmount;
          }
          break;
          case GetBillGroup.UPCOMING_BILLS: {
            this.combinedDueAmount = this.billsAggregatedInfo.upcomingAllDueAmount;
          }
          break;
        }
      }
      break;
      case GetBillDuration.MONTH: {
        switch (this.billGroupSelected) {
          case GetBillGroup.RECENT_BILLS: {
            this.combinedDueAmount = this.billsAggregatedInfo.recentMonthlyDueAmount;
          }
          break;
          case GetBillGroup.UPCOMING_BILLS: {
            this.combinedDueAmount = this.billsAggregatedInfo.upcomingMonthlyDueAmount;
          }
          break;
        }
      }
      break;
      case GetBillDuration.WEEK: {
        switch (this.billGroupSelected) {
          case GetBillGroup.RECENT_BILLS: {
            this.combinedDueAmount = this.billsAggregatedInfo.recentWeeklyDueAmount;
          }
          break;
          case GetBillGroup.UPCOMING_BILLS: {
            this.combinedDueAmount = this.billsAggregatedInfo.upcomingWeeklyDueAmount;
          }
          break;
        }
      }
      break;
    }
  }

  onPayNowClick(bill: ViewBillType) {
    this.logger.log('Pay now button clicked for bill: ', bill);
    this.billService.presentPaymentCombinedModal(bill);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
